import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, ChevronRight, ChevronLeft, Building, User, FileText, CreditCard, Loader2, Zap, AlertTriangle, Home, MapPin, Ruler, Construction, PartyPopper, Clock, CheckCircle2, Star, Lock, Coins, Building2, Search } from 'lucide-react';
import { API_URL } from '../config';
import Header from './Header';
import Footer from './Footer';
import Pricing from './Pricing';
import About from './About';
import FAQ from './FAQ';
import HomeGuide from './HomeGuide';
import TrustSection from './TrustSection';
import { ANCPI_DATA } from '../ancpiData';
import { formatPrice } from '../utils/formatPrice';
import { trackEvent, buildItem } from '../utils/analytics';
import { getAttribution } from '../utils/attribution';

// Toast Notification Component (floating bottom - elegant pill design)
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000); // Auto-close după 5s
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed bottom-24 left-1/2 z-50
                     px-5 py-3 rounded-full shadow-lg text-white text-sm font-medium
                     flex items-center gap-2 backdrop-blur-sm
                     transition-all duration-300 ease-out
                     animate-fade-in
                     ${type === 'error' ? 'bg-red-500/95' : 'bg-emerald-500/95'}`}
         style={{ transform: 'translateX(-50%)', willChange: 'transform, opacity' }}>
      <span className="text-base font-bold">{type === 'error' ? '✕' : '✓'}</span>
      <span className="max-w-xs sm:max-w-md">{message}</span>
    </div>
  );
};

// Modal Sistată - popup pentru numere cadastrale inactive
const SistataModal = ({ isOpen, onContinue, onCancel, upgradePrice, wantsUpgrade, setWantsUpgrade, nrCadastral, judet, uat, propertyType, address, area }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fade-in">
        {/* Header cu icon */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-yellow-600" aria-hidden="true" />
          </div>
          <h3 className="text-xl font-bold text-gray-900">SISTATĂ</h3>
        </div>

        {/* Mesaj explicativ */}
        <p className="text-gray-600 mb-4">
          Identificatorul cu numărul <strong>{nrCadastral}</strong> a fost găsit, dar cartea funciară este <strong className="text-red-600">sistată</strong>.
          <br /><br />
          Nu-ți face griji — vom identifica numărul activ și vom emite extrasul actualizat pentru tine.
        </p>

        {/* Detalii proprietate din ANCPI */}
        {(propertyType || address || area) && (
          <div className="text-sm text-gray-600 space-y-0.5 mb-4 ml-1">
            {propertyType && (
              <div className="grid grid-cols-[1.2rem_1fr] gap-x-1 items-baseline">
                <span><Home className="w-4 h-4 inline-block align-text-bottom" aria-hidden="true" /></span>
                <span>Tip: <span className="font-medium text-gray-800">{propertyType}</span></span>
              </div>
            )}
            {address && (
              <div className="grid grid-cols-[1.2rem_1fr] gap-x-1 items-baseline">
                <span><MapPin className="w-4 h-4 inline-block align-text-bottom" aria-hidden="true" /></span>
                <span>Adresă: <span className="font-medium text-gray-800">{address}</span></span>
              </div>
            )}
            {area && (
              <div className="grid grid-cols-[1.2rem_1fr] gap-x-1 items-baseline">
                <span><Ruler className="w-4 h-4 inline-block align-text-bottom" aria-hidden="true" /></span>
                <span>Suprafață: <span className="font-medium text-gray-800">{area} mp</span></span>
              </div>
            )}
          </div>
        )}

        {/* Checkbox upgrade */}
        <label className="flex items-center gap-3 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl cursor-pointer hover:bg-yellow-100 transition-colors mb-4">
          <input
            type="checkbox"
            checked={wantsUpgrade}
            onChange={(e) => setWantsUpgrade(e.target.checked)}
            className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 flex-shrink-0"
          />
          <span className="text-sm font-semibold text-gray-900">Vreau extrasul cu numărul actualizat</span>
        </label>

        {/* Cost total - separat */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-6 text-center">
          <p className="text-lg font-bold text-emerald-800">Cost total: {formatPrice(upgradePrice)} RON</p>
          <p className="text-xs text-emerald-600">taxa ANCPI inclusă</p>
        </div>

        {/* Butoane */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Anulează
          </button>
          <button
            onClick={onContinue}
            disabled={!wantsUpgrade}
            className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            Continuă
          </button>
        </div>
      </div>
    </div>
  );
};

const CertificateWizard = () => {
  const [step, setStep] = useState(1);
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  // Toast notification state (înlocuiește error/success)
  const [toast, setToast] = useState(null);

  // Flash vizual pentru ghidare UX (fără auto-focus)
  const [flashElement, setFlashElement] = useState(null);

  // Ref pentru progress bar (mobile scroll după Continuare)
  const progressBarRef = useRef(null);
  // Ref pentru buton Continuare (mobile scroll după selectare certificat)
  const continueButtonRef = useRef(null);
  // Ref pentru buton Plătește (mobile scroll la step 5)
  const paymentButtonRef = useRef(null);

  // Încarcă prețuri INSTANT din localStorage (elimină flash complet!)
  const [prices, setPrices] = useState(() => {
    // 1. Prețuri injectate la build de scripts/prerender.js (din API) → corecte în HTML-ul pre-randat (SEO)
    if (typeof window !== 'undefined' && window.__INITIAL_PRICES__) return window.__INITIAL_PRICES__;
    // 2. Cache din vizita anterioară
    try {
      const cached = localStorage.getItem('ancpi_prices');
      if (cached) return JSON.parse(cached);
    } catch (e) { /* localStorage indisponibil */ }
    // 3. Fallback (actualizează dacă se schimbă prețurile din admin)
    return { basic: 99.86, withHistory: 99.7, carteFunciara: 99.86, planCadastral: 99.7, upgradeSistata: 200, identificareAdresa: 200 };
  });

  const [formData, setFormData] = useState({
    userType: '',
    cui: '',
    cnp: '',
    companyData: null,
    certificateType: 'carteFunciara', // DEFAULT: Carte Funciară pre-selectată
    certificatePurpose: 'Informare', // Default "Informare" pentru a reduce fricțiunea
    billingType: '',
    billingData: {},
    contactData: { fullName: '', email: '', phone: '' },
    // Metodă identificare imobil
    identificationType: 'cadastral', // DEFAULT: "Știu numărul CF/Cadastral" pre-selectat
    // Date imobil Step 2 - pentru bot ANCPI
    propertyData: {
      judet: '',
      judetId: '',
      uat: '',
      uatId: '',
      nrCadastral: '',       // DOAR pentru identificationType = 'cadastral'
      ownerName: '',         // DOAR pentru identificationType = 'address'
      fullAddress: '',       // DOAR pentru identificationType = 'address'
      necesarLa: ''          // Data când e necesar documentul (opțional)
    }
  });
  const [isVerifyingCUI, setIsVerifyingCUI] = useState(false);
  const [isVerifyingCadastral, setIsVerifyingCadastral] = useState(false);
  const [verificationSeconds, setVerificationSeconds] = useState(0);
  const [, setCadastralVerified] = useState(null); // null = not checked, true = exists, false = not found
  const [cadastralStatus, setCadastralStatus] = useState(null); // 'Activa' sau 'Sistata'
  const [newCadastralNumber, setNewCadastralNumber] = useState(null); // Numărul nou (dacă e sistată)
  const [wantsUpdatedNumber, setWantsUpdatedNumber] = useState(false); // Checkbox pentru +200 RON
  const [showSistataModal, setShowSistataModal] = useState(false); // Modal pentru numere cadastrale sistată
  const [acceptedTerms, setAcceptedTerms] = useState(false); // FIX #27 - Checkbox Termeni și Condiții (obligatoriu)
  const [acceptedMarketing, setAcceptedMarketing] = useState(false); // FIX #27 - Checkbox comunicări comerciale (opțional)

  // Comandă nefinalizată: token-ul draftului salvat la intrarea în Confirmare (pentru o singură reamintire pe email)
  const draftTokenRef = useRef(null);

  // Anunț wizard (setabil din admin - Feature Flags) - afișat ca TEXT simplu
  const [wizardNotice, setWizardNotice] = useState(null);

  // Cod de reducere (validat + recalculat SERVER-SIDE la creare comandă)
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null); // { code, discountAmount, newPrice }
  const [discountError, setDiscountError] = useState('');
  const [discountLoading, setDiscountLoading] = useState(false);
  const [urlDiscount, setUrlDiscount] = useState(null); // din ?cod= : { code, carteFunciara:{amount,newPrice}, planCadastral:{amount,newPrice} }
  const [urlCodeError, setUrlCodeError] = useState(''); // cod din URL invalid
  const [discountDismissed, setDiscountDismissed] = useState(false); // user a apăsat „Elimină"

  // Validare live pentru Step 4 - Date Contact
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [nameError, setNameError] = useState('');

  // Validare live pentru număr cadastral (Step 2)
  const [cadastralFormatError, setCadastralFormatError] = useState('');

  // Funcție validare format număr cadastral
  const validateCadastralFormat = (value) => {
    if (!value.trim()) {
      return ''; // Gol = fără eroare (încă nu a scris nimic)
    }

    // Detectează cuvinte interzise (text în loc de număr)
    const forbiddenWords = /carte|funciara|cadastral|nr\.|numar|număr/i;
    if (forbiddenWords.test(value)) {
      return 'Introduceți DOAR numărul (ex: 12345 sau 12345-C1-U1), fără text adițional';
    }

    // Format valid: 12345, 12345-C1-U1, 2370/19, 2370/N, 237:5,9/4, 123/6;5:9.6
    const validFormat = /^[0-9]+([/:;,.-][0-9A-Za-z]+)*(-C[0-9]+-U[0-9]+)?$/i;
    if (!validFormat.test(value.trim())) {
      return 'Format invalid. Exemple valide: 12345, 203242, 5724-C1-U1';
    }

    // Verifică segmentele după cratimă (maxim 4 caractere fiecare)
    if (value.includes('-')) {
      const segments = value.split('-');
      for (let i = 1; i < segments.length; i++) {
        if (segments[i].length > 4) {
          return 'Format invalid. Exemple valide: 12345, 203242, 5724-C1-U1';
        }
      }
    }

    return ''; // Valid
  };

  useEffect(() => {
    fetchPrices();
    fetchFeatures(); // Check maintenance mode
  }, []);

  // Citește cod de reducere din URL (?cod=XX) și îl validează SERVER-SIDE pentru CF + PC.
  // Cod fals/expirat → validarea pică → banner galben, wizard normal, zero efect pe preț.
  // Folosit și de popup-ul „cadou la ieșire” (evenimentul applyGiftCode, 03.11.2026) - aceeași validare, fără reload
  const loadUrlDiscount = async (rawCod) => {
    const cod = String(rawCod || '').trim().toUpperCase();
    if (!cod) return;
    if (!/^[A-Z0-9]{3,20}$/.test(cod)) { setUrlCodeError(cod.slice(0, 20)); return; }
    try {
      const types = ['carteFunciara', 'planCadastral'];
      const results = await Promise.all(types.map(async (t) => {
        const res = await fetch(`${API_URL}/api/orders/validate-discount`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: cod, certificateType: t })
        });
        return { t, data: await res.json() };
      }));
      const dd = {};
      let code = null;
      results.forEach(({ t, data }) => {
        if (data.valid && data.discountAmount > 0) {
          dd[t] = { amount: data.discountAmount, newPrice: data.newPrice };
          code = data.code;
        }
      });
      if (code) { setUrlCodeError(''); setUrlDiscount({ code, ...dd }); }
      else setUrlCodeError(cod);
    } catch (e) { /* silent - wizard-ul merge normal fără reducere */ }
  };

  useEffect(() => {
    loadUrlDiscount(new URLSearchParams(window.location.search).get('cod'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Popup cadou (03.11.2026): „Aplică reducerea acum” → codul se validează și se aplică fără reload
  useEffect(() => {
    window.__cfWizardReady = true;
    const onGift = (e) => {
      const code = e && e.detail && e.detail.code;
      if (typeof code !== 'string') return;
      setDiscountDismissed(false);
      loadUrlDiscount(code);
    };
    window.addEventListener('applyGiftCode', onGift);
    return () => {
      window.__cfWizardReady = false;
      window.removeEventListener('applyGiftCode', onGift);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Popup cadou: nu apare dacă formularul are deja o reducere (din link sau introdusă manual)
  useEffect(() => {
    window.__cfHasDiscount = !!(appliedDiscount || urlDiscount);
  }, [appliedDiscount, urlDiscount]);

  // Revenire din emailul „comandă nefinalizată": încarcă datele salvate și deschide direct Confirmarea.
  // Tokenul a fost scos din URL în index.js (nu ajunge în analytics). Draft inexistent/expirat → wizard normal.
  useEffect(() => {
    const token = window.__cfResumeToken;
    if (!token) return;
    window.__cfResumeToken = null;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/orders/draft/${token}`);
        const json = await res.json();
        if (!res.ok || !json.success || !json.data) return;
        const d = json.data;
        if (!['carteFunciara', 'planCadastral'].includes(d.certificateType)) return;
        setFormData((prev) => ({
          ...prev,
          certificateType: d.certificateType,
          certificatePurpose: d.certificatePurpose || prev.certificatePurpose,
          userType: d.userType || prev.userType,
          billingType: d.billingType || prev.billingType,
          cui: d.cui || prev.cui,
          companyData: d.companyData && Object.keys(d.companyData).length ? d.companyData : prev.companyData,
          billingData: d.billingData || prev.billingData,
          contactData: { ...prev.contactData, ...(d.contactData || {}) },
          identificationType: 'cadastral',
          propertyData: { ...prev.propertyData, ...(d.propertyData || {}) }
        }));
        setCadastralStatus('Activa');
        setWantsUpdatedNumber(false);
        draftTokenRef.current = token;
        setStep(4);
        setTimeout(() => {
          if (progressBarRef.current) progressBarRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
      } catch (e) { /* silent - wizard-ul merge normal */ }
    })();
  }, []);

  // Auto-aplică reducerea din URL la pasul Confirmare (dacă nu a fost eliminată manual)
  useEffect(() => {
    const simple = formData.identificationType !== 'address' && !(cadastralStatus === 'Sistata' && wantsUpdatedNumber);
    if (step === 4 && urlDiscount && simple && !appliedDiscount && !discountDismissed) {
      const d = urlDiscount[formData.certificateType];
      if (d && d.amount > 0) {
        setAppliedDiscount({ code: urlDiscount.code, discountAmount: d.amount, newPrice: d.newPrice });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, urlDiscount, formData.certificateType, appliedDiscount, discountDismissed]);

  // Cronometru pentru verificare cadastrală (Step 1)
  useEffect(() => {
    let interval = null;
    if (isVerifyingCadastral) {
      setVerificationSeconds(0);
      interval = setInterval(() => {
        setVerificationSeconds(s => s + 1);
      }, 1000);
    } else {
      setVerificationSeconds(0);
    }
    return () => clearInterval(interval);
  }, [isVerifyingCadastral]);

  // Flash automat la Step 3 și 4
  useEffect(() => {
    // NU mai resetăm identificationType - rămâne 'cadastral' by default

    if (step === 4) {
      // Auto-completare Nume din facturare (pentru PF)
      if (formData.billingType === 'individual' && formData.billingData.name && !formData.contactData.fullName) {
        setFormData({
          ...formData,
          contactData: { ...formData.contactData, fullName: formData.billingData.name }
        });
      }

      // Flash secvențial: Nume (200ms) → Email (1000ms = 200 + 800)
      // Pentru PF: skip nume (deja completat), flash doar email
      if (formData.billingType !== 'individual') {
        setTimeout(() => triggerFlash('contact-name-input'), 200);
      }
      setTimeout(() => triggerFlash('contact-email-input'), formData.billingType === 'individual' ? 200 : 1000);
    }
    if (step === 5) {
      setTimeout(() => triggerFlash('terms-checkbox'), 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // GA4 funnel: view_item la pasul 2 (Tip extras), begin_checkout la pasul 3 (Facturare)
  useEffect(() => {
    if (step === 2) {
      const cf = prices.carteFunciara || prices.basic;
      const pc = prices.planCadastral || prices.withHistory;
      trackEvent('view_item', {
        currency: 'RON',
        value: Number(cf) || 0,
        items: [buildItem('carteFunciara', cf), buildItem('planCadastral', pc)]
      });
    }
    if (step === 3) {
      const price = getCurrentPrice();
      trackEvent('begin_checkout', {
        currency: 'RON',
        value: Number(price) || 0,
        items: [buildItem(formData.certificateType, price, getVariant())]
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // AUTO-SAVE date facturare în localStorage (PF + PJ) - debounced 500ms
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // PF - Salvează: name, county, city, street, streetNumber, email, phone
      if (formData.billingType === 'individual') {
        const pfData = {
          name: formData.billingData.name || '',
          county: formData.billingData.county || '',
          city: formData.billingData.city || '',
          street: formData.billingData.street || '',
          streetNumber: formData.billingData.streetNumber || '',
          email: formData.contactData.email || '',
          phone: formData.contactData.phone || ''
        };
        // Salvează DOAR dacă există cel puțin un câmp completat (evită save gol)
        if (Object.values(pfData).some(val => val !== '')) {
          localStorage.setItem('cf_extras_pf_data', JSON.stringify(pfData));
        }
      }

      // PJ - Salvează: cui, name, fullName, email, phone
      if (formData.billingType === 'otherCompany') {
        const pjData = {
          cui: formData.billingData.cui || '',
          name: formData.billingData.name || '',
          fullName: formData.contactData.fullName || '',
          email: formData.contactData.email || '',
          phone: formData.contactData.phone || ''
        };
        // Salvează DOAR dacă există cel puțin un câmp completat
        if (Object.values(pjData).some(val => val !== '')) {
          localStorage.setItem('cf_extras_pj_data', JSON.stringify(pjData));
        }
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.billingType, formData.billingData, formData.contactData]);

  const fetchPrices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/orders/prices`);
      const data = await response.json();

      // Salvează în localStorage pentru încărcare INSTANT la următoarea vizită
      localStorage.setItem('ancpi_prices', JSON.stringify(data));

      // Actualizează state (silent update în background)
      setPrices(data);
    } catch (err) {
      console.error('Error fetching prices:', err);
    }
  };

  const fetchFeatures = async () => {
    try {
      const response = await fetch(`${API_URL}/api/orders/features`);
      const data = await response.json();
      setMaintenanceMode(data.maintenanceMode || false);
      if (data.wizardNotice?.enabled && typeof data.wizardNotice.text === 'string' && data.wizardNotice.text.trim()) {
        setWizardNotice(data.wizardNotice.text.trim());
      } else {
        setWizardNotice(null);
      }
    } catch (err) {
      console.error('Error fetching features:', err);
      // Fallback: dacă eroare API, permitem access (fail-open pentru UX)
      setMaintenanceMode(false);
    }
  };

  /**
   * Flash vizual helper - trigger animație pe element
   */
  const triggerFlash = (elementId) => {
    setFlashElement(elementId);
    setTimeout(() => setFlashElement(null), 1500); // Clear după 1.5s
  };

  /**
   * NOUĂ FUNCȚIE - Verifică CUI prin ListaFirme API
   */
  const handleVerifyCUI = async (cuiToVerify = null, isForBilling = false) => {
    const cui = cuiToVerify || formData.cui;

    if (!cui) {
      setToast({ message: 'Introduceți un CUI valid', type: 'error' });
      return;
    }

    // FIX #27 - Salvăm userType la start pentru a verifica dacă s-a schimbat între timp
    const currentUserType = formData.userType;

    setIsVerifyingCUI(true);
    setToast(null); // Clear orice toast anterior

    try {
      // Folosim noul endpoint ListaFirme
      const response = await fetch(`${API_URL}/api/company/verify/${cui}`);
      const data = await response.json();

      // FIX #27 - Verificăm dacă user-ul a schimbat tab-ul între timp
      if (formData.userType !== currentUserType) {
        console.log('User switched tabs during CUI verification - ignoring result');
        return; // User a schimbat tab-ul, nu afișăm mesajul
      }

      if (data.success && data.company) {
        const companyInfo = {
          name: data.company.name,
          cui: data.company.cui,
          registrationNumber: data.company.registrationNumber,
          address: data.company.address,
          county: data.company.county,
          city: data.company.city,
          phone: data.company.phone?.[0] || '',
          email: data.company.email?.[0] || '',
          isActive: data.company.isActive
        };

        // Toast success
        setToast({ message: 'Datele firmei au fost găsite cu succes!', type: 'success' });

        // Dacă e pentru facturare (Step 3)
        if (isForBilling) {
          setFormData({
            ...formData,
            billingData: {
              type: 'company',
              name: companyInfo.name,
              cui: companyInfo.cui,
              registrationNumber: companyInfo.registrationNumber,
              address: companyInfo.address,
              county: companyInfo.county,
              city: companyInfo.city
            }
          });
        } else {
          // Dacă e pentru comanda principală (Step 1)
          setFormData({
            ...formData,
            companyData: companyInfo
          });
          // Mobile: Scroll la butonul Continuare după verificare CUI
          setTimeout(() => {
            if (window.innerWidth < 1024 && continueButtonRef.current) {
              continueButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 200);
        }

      } else {
        setToast({ message: data.error || 'CUI invalid sau firmă negăsită în baza de date', type: 'error' });
      }
    } catch (err) {
      console.error('Error verifying CUI:', err);
      setToast({ message: 'Eroare la verificarea CUI. Verificați conexiunea la internet.', type: 'error' });
    } finally {
      setIsVerifyingCUI(false);
    }
  };

  /**
   * Validare live pentru Nume (Step 4)
   */
  const validateNameLive = (name) => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      setNameError('');
      return;
    }

    // Regex: Doar litere (inclusiv diacritice), spații și cratimă
    const validCharsRegex = /^[a-zA-ZăâîșțĂÂÎȘȚ\s-]+$/;

    // Verificare caractere invalide (cifre, puncte, @, #, etc.)
    if (!validCharsRegex.test(trimmedName)) {
      setNameError('Doar litere, spații și cratimă (-)');
      return;
    }

    // Split pe spații și elimină elemente goale
    const words = trimmedName.split(/\s+/).filter(w => w.length > 0);

    // Verificare minim 2 cuvinte (Nume + Prenume)
    if (words.length < 2) {
      setNameError('Introduceți Nume și Prenume (minim 2 cuvinte)');
      return;
    }

    // Verificare fiecare cuvânt să aibă minim 3 caractere
    const invalidWord = words.find(w => w.length < 3);
    if (invalidWord) {
      setNameError('Fiecare cuvânt trebuie să aibă minim 3 caractere');
      return;
    }

    setNameError(''); // Valid!
  };

  /**
   * Validare live pentru Email (Step 4)
   */
  const validateEmailLive = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!email) {
      setEmailError('');
      return;
    }

    if (email.includes('..')) {
      setEmailError('Email invalid: puncte duble consecutive');
    } else if (email.includes(' ')) {
      setEmailError('Email invalid: nu poate conține spații');
    } else if (!emailRegex.test(email)) {
      setEmailError('Email invalid: format incorect');
    } else {
      setEmailError(''); // Valid!
    }
  };

  /**
   * Validare live pentru Telefon (Step 4)
   */
  const validatePhoneLive = (phone) => {
    const phoneRegex = /^(07|02)\d{8}$/;
    const trimmedPhone = phone.trim();

    if (!trimmedPhone) {
      setPhoneError('');
      return;
    }

    if (!phoneRegex.test(trimmedPhone)) {
      setPhoneError('Format invalid: 07... sau 02... (10 cifre)');
    } else {
      setPhoneError(''); // Valid!
    }
  };

  // Comandă simplă CF/PC (codul de reducere se aplică DOAR aici, nu la Sistată/adresă)
  const isSimpleOrder = formData.identificationType !== 'address' && !(cadastralStatus === 'Sistata' && wantsUpdatedNumber);

  // ==========================================
  // GA4 funnel - preț + variantă curentă (aceeași logică ca totalul de la Confirmare)
  // Zero date personale; trackEvent nu trimite nimic fără consimțământ (gtag neîncărcat)
  // ==========================================
  const getCurrentPrice = () => (
    formData.identificationType === 'address'
      ? (prices.identificareAdresa || 200)
      : cadastralStatus === 'Sistata' && wantsUpdatedNumber
        ? (prices.upgradeSistata || 200)
        : (formData.certificateType === 'carteFunciara' ? (prices.carteFunciara || prices.basic) : (prices.planCadastral || prices.withHistory))
  );
  const getVariant = () => (
    formData.identificationType === 'address' ? 'Identificare adresă'
      : (cadastralStatus === 'Sistata' && wantsUpdatedNumber ? 'Sistată' : 'Standard')
  );

  const handleApplyDiscount = async () => {
    const code = discountCode.trim().toUpperCase();
    if (!code) return;
    if (!/^[A-Z0-9]{3,20}$/.test(code)) {
      setAppliedDiscount(null);
      setDiscountError('Cod invalid sau expirat.');
      return;
    }
    setDiscountLoading(true);
    setDiscountError('');
    try {
      const res = await fetch(`${API_URL}/api/orders/validate-discount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          certificateType: formData.certificateType,
          email: formData.contactData.email || undefined
        })
      });
      const data = await res.json();
      if (data.valid) {
        setAppliedDiscount({ code: data.code, discountAmount: data.discountAmount, newPrice: data.newPrice });
      } else {
        setAppliedDiscount(null);
        setDiscountError(data.error || 'Cod invalid sau expirat.');
      }
    } catch (e) {
      setAppliedDiscount(null);
      setDiscountError('Eroare la verificarea codului.');
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
    setDiscountError('');
    setDiscountDismissed(true);
  };

  const handleSubmitOrder = async () => {
    // GA4: add_payment_info (click „Plătește cu card") - prețul final afișat, cu reducerea aplicată.
    // Redirect-ul spre Stripe AȘTEAPTĂ trimiterea (max 1.5s), altfel evenimentul se pierde la plecarea din pagină.
    // Fără consimțământ (gtag neîncărcat) → nu se trimite nimic și nu se așteaptă.
    const paymentInfoSent = new Promise((resolve) => {
      const basePrice = getCurrentPrice();
      const finalPrice = (appliedDiscount && isSimpleOrder) ? appliedDiscount.newPrice : basePrice;
      const sent = trackEvent('add_payment_info', {
        currency: 'RON',
        value: Number(finalPrice) || 0,
        payment_type: 'card',
        ...(appliedDiscount && isSimpleOrder ? { coupon: appliedDiscount.code } : {}),
        items: [buildItem(formData.certificateType, finalPrice, getVariant())],
        transport_type: 'beacon',
        event_callback: () => resolve(),
        event_timeout: 1500
      });
      if (!sent) resolve();
      setTimeout(resolve, 1600); // plasă de siguranță
    });

    // Log analytics în backend (actualizat pentru ANCPI)
    const identifier = formData.nrCadastral || formData.email || 'N/A';
    const userType = formData.userType === 'company' ? 'PJ' : 'PF';
    const extractType = formData.certificateType === 'carteFunciara' ? 'CF' : 'PC';
    let orderType = extractType;
    if (formData.identificationType === 'address') {
      orderType += ' Identificare';
    } else if (formData.cadastralStatus === 'Sistata') {
      orderType += ' Sistată';
    } else {
      orderType += ' Simplă';
    }

    fetch(`${API_URL}/api/orders/analytics/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event: `💳 [WIZARD] ${identifier} (${userType}, ${orderType}) - Click "Plătește cu cardul"`,
        data: { step: 5, identifier, userType, orderType }
      })
    }).catch(() => {}); // Silent fail

    setIsVerifyingCUI(true);  // Folosim CUI loading pentru submit (generic)
    setToast(null); // Clear orice toast anterior

    try {
      // Adăugăm date suplimentare pentru ANCPI (status cadastral + upgrade)
      const orderData = {
        ...formData,
        acceptedMarketing: acceptedMarketing,
        // Tip extras pentru routing bot (CF/PC)
        extractType: formData.certificateType === 'carteFunciara' ? 'CF' : 'PC',
        // Date cadastral (Sistată + upgrade)
        propertyData: {
          ...formData.propertyData,
          cadastralStatus: cadastralStatus,         // 'Activa' / 'Sistata' / null
          newCadastralNumber: newCadastralNumber    // Nr. nou (SECRET - dacă bot l-a găsit)
        },
        upgradedCadastral: wantsUpdatedNumber,      // Checkbox upgrade bifat?
        // Cod reducere - prețul final e recalculat SERVER-SIDE
        discountCode: (appliedDiscount && isSimpleOrder) ? appliedDiscount.code : undefined,
        // Sursa vizitei (first touch în sesiune) - curățată pe server
        attribution: getAttribution(),
        // Comanda nefinalizată devine „convertită" (nu mai primește reamintire)
        draftToken: draftTokenRef.current || undefined
      };

      const response = await fetch(`${API_URL}/api/orders/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData)
      });
      const data = await response.json();

      if (data.success && data.checkoutUrl) {
        // Redirectează către Stripe Checkout (după ce evenimentul GA4 a plecat sau după max 1.6s)
        console.log('Redirectare către Stripe Checkout...');
        await paymentInfoSent;
        window.location.href = data.checkoutUrl;
      } else {
        setToast({ message: data.error || 'Eroare la crearea comenzii', type: 'error' });
        setIsVerifyingCUI(false);
      }
    } catch (err) {
      setToast({ message: 'Eroare la procesarea comenzii: ' + err.message, type: 'error' });
      setIsVerifyingCUI(false);
    }
  };

  const steps = [
    { num: 1, title: 'Date imobil', icon: Building },
    { num: 2, title: 'Tip extras', icon: FileText },
    { num: 3, title: 'Facturare și Contact', icon: User },
    { num: 4, title: 'Confirmare', icon: CheckCircle }
  ];

  const canProceed = () => {
    switch(step) {
      case 1:
        // Step 1: Date imobil - validare câmpuri completate (verificarea se face la click Continuare)
        if (formData.identificationType === 'cadastral') {
          const hasBasicData = formData.propertyData.judet &&
                               formData.propertyData.uat &&
                               formData.propertyData.nrCadastral.trim().length > 0 &&
                               !cadastralFormatError; // Fără erori de format
          return hasBasicData;
        } else {
          // Validare pentru identificare după adresă
          return formData.propertyData.ownerName.trim().length >= 3 &&
                 formData.propertyData.judet &&
                 formData.propertyData.uat &&
                 formData.propertyData.fullAddress.trim().length >= 10;
        }
      case 2:
        // Step 2: Tip extras (Carte Funciară / Plan Cadastral)
        return formData.certificateType;
      case 3:
        // Step 3: Date factura + Contact
        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const email = formData.contactData.email || '';

        // Validare date facturare
        if (!formData.billingType) return false;
        if (formData.billingType === 'sameCompany') {
          // PJ - factura pe aceeași firmă + email + nume contact obligatoriu
          const fullName = (formData.contactData.fullName || '').trim();
          return email && emailRegex.test(email) && fullName.length >= 3;
        }
        if (formData.billingType === 'otherCompany') {
          // PJ - factura pe altă firmă + email + nume contact obligatoriu
          const fullName = (formData.contactData.fullName || '').trim();
          return formData.billingData.name && email && emailRegex.test(email) && fullName.length >= 3;
        }
        if (formData.billingType === 'individual') {
          // PF - validare date facturare + email (nume deja în billingData)
          const name = (formData.billingData.name || '').trim();
          const address = (formData.billingData.address || '').trim();
          return name.length >= 3 && address.length >= 5 && email && emailRegex.test(email);
        }

        return false;
      default:
        return true;
    }
  };

  const scrollToWizard = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setStep(1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Maintenance Mode Banner */}
      {maintenanceMode && (
        <div className="bg-yellow-500 text-yellow-900 py-4 px-4 text-center font-semibold shadow-lg">
          <Construction className="w-5 h-5 inline-block align-text-bottom mr-1" aria-hidden="true" />Platformă în mentenanță. Comenzile noi sunt temporar dezactivate. Revenim curând!
        </div>
      )}

      <div className="max-w-7xl mx-auto py-12 px-4">
        {/* H1 pentru SEO (vizibil pentru screen readers și motoare căutare) */}
        <h1 className="sr-only">Extras Carte Funciară Online – Livrare pe Email în 5 Minute</h1>

        {/* Layout Desktop: 25% Hero | 40% Formular | 35% Specimen (stack pe mobile) */}
        {/* La step 2+ imaginea dispare și wizard-ul se extinde moderat */}
        <div className={`grid grid-cols-1 gap-6 transition-all duration-500 ease-in-out ${
          step === 1
            ? 'lg:grid-cols-[25%_40%_35%]'
            : 'lg:grid-cols-[25%_55%_20%]'
        }`}>

          {/* Coloana STÂNGA - Hero Section (30%) */}
          <div className="order-4 lg:order-1 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl shadow-lg p-6 lg:p-8 border-2 border-emerald-200">
            {/* Tagline mare cu badge "5 minute" */}
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 text-center mb-2">
              Extras Carte Funciară în
            </h2>
            <div className="flex items-center justify-center mb-6">
              <div className="inline-flex items-center justify-center px-6 py-3 bg-blue-50 border-2 border-emerald-600 rounded-2xl shadow-xl">
                <span className="text-emerald-600 font-bold text-4xl text-center">5 minute</span>
              </div>
            </div>

            {/* 5 Bullet points */}
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm lg:text-base text-gray-800 font-medium">Extrase emise direct de ANCPI online</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm lg:text-base text-gray-800 font-medium">Documente oficiale 100% valabile</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm lg:text-base text-gray-800 font-medium">Eliberare complet automatizată</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm lg:text-base text-gray-800 font-medium">Livrare pe email și SMS</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm lg:text-base text-gray-800 font-medium">Extras și ortofotoplan oficial ANCPI</span>
              </li>
            </ul>

            {/* Badge-uri Stripe + SSL */}
            <div className="flex items-center justify-center gap-4">
              <img src="/stripe.png" alt="Powered by Stripe" className="h-10 w-auto opacity-80 hover:opacity-100 transition-opacity" width="200" height="200" />
              <img src="/ssl.png" alt="SSL Secure" className="h-10 w-auto opacity-80 hover:opacity-100 transition-opacity" width="867" height="503" />
            </div>
          </div>

          {/* Coloana CENTRU - Wizard Card */}
          {/* Text separator mobil — între specimen și formular */}
          <p className="order-2 lg:hidden text-center text-lg font-semibold text-emerald-700 py-2">
            Extras Carte Funciară ANCPI
          </p>

          <div className="order-3 lg:order-2 relative z-10 bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col">
            {/* Progress Bar Orizontal - Mobile Only (ascuns pe desktop) */}
            <div ref={progressBarRef} className="lg:hidden bg-gradient-to-r from-emerald-600 to-emerald-600 px-8 py-6">
              <div className="flex justify-between items-center">
                {steps.map((s, idx) => (
                  <React.Fragment key={s.num}>
                    <div className="flex flex-col items-center flex-1">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                        step >= s.num ? 'bg-white text-emerald-600' : 'bg-teal-500 text-white'
                      }`}>
                        {step > s.num ? <CheckCircle className="w-6 h-6" /> : <span className="font-bold">{s.num}</span>}
                      </div>
                    </div>
                    {idx < steps.length - 1 && (
                      <div className={`h-1 flex-1 mx-2 transition-all duration-300 ${step > s.num ? 'bg-white' : 'bg-teal-500'}`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Layout intern wizard - Desktop Only */}
            <div>
              {/* Progress Bar Orizontal - Desktop Only (sus) */}
              <div className="hidden lg:block border-b border-gray-200 bg-gray-50 px-6 py-4">
                <div className="flex items-center">
                  {steps.map((s, idx) => (
                    <React.Fragment key={s.num}>
                      <div className="flex flex-col items-center">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${
                          step >= s.num
                            ? 'bg-emerald-600 text-white'
                            : 'bg-gray-200 text-gray-500'
                        }`}>
                          {step > s.num ? (
                            <CheckCircle className="w-5 h-5" />
                          ) : (
                            <span className="font-bold text-sm">{s.num}</span>
                          )}
                        </div>
                        <span className={`mt-1 text-[11px] font-medium transition-all duration-300 ${
                          step === s.num
                            ? 'text-emerald-600 font-semibold'
                            : step > s.num
                            ? 'text-gray-500'
                            : 'text-gray-400'
                        }`}>
                          {s.title}
                        </span>
                      </div>
                      {/* Linie între pași - aliniată cu cercurile */}
                      {idx < steps.length - 1 && (
                        <div className={`flex-1 h-0.5 mx-3 -mt-5 transition-all duration-300 ${
                          step > s.num ? 'bg-emerald-600' : 'bg-gray-200'
                        }`} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Banner cod reducere din URL (?cod=): activ (verde) sau invalid (galben) */}
              {urlDiscount ? (
                <div className="bg-emerald-50 border-b border-emerald-200 px-4 lg:px-6 py-3 text-center">
                  <span className="text-sm font-semibold text-emerald-800">
                    <PartyPopper className="w-4 h-4 inline-block align-text-bottom mr-1" aria-hidden="true" />Ai o reducere activă: <span className="font-mono font-bold">{urlDiscount.code}</span>
                  </span>
                </div>
              ) : urlCodeError ? (
                <div className="bg-amber-50 border-b border-amber-200 px-4 lg:px-6 py-3 text-center">
                  <span className="text-sm font-semibold text-amber-800">
                    <AlertTriangle className="w-4 h-4 inline-block align-text-bottom mr-1" aria-hidden="true" />Codul <span className="font-mono font-bold">{urlCodeError}</span> nu există sau nu mai este valid.
                  </span>
                </div>
              ) : null}

              {/* Anunț setabil din admin (text simplu, NU HTML) */}
              {wizardNotice && (
                <div className="bg-yellow-50 border-b border-yellow-300 px-4 lg:px-6 py-3">
                  <p className="text-sm font-medium text-yellow-900 text-center whitespace-pre-line">
                    <Clock className="w-4 h-4 inline-block align-text-bottom mr-1" aria-hidden="true" />{wizardNotice}
                  </p>
                </div>
              )}

              {/* Form Content */}
              <div className="p-8">
            {/* Step 2 - Alegere tip extras */}
            {step === 2 && (
              <div className="space-y-6">
                {/* Text confirmare imobil identificat - diferit pentru SISTATĂ */}
                {cadastralStatus === 'Sistata' && wantsUpdatedNumber ? (
                  <div className="space-y-2">
                    <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 space-y-2">
                    <p className="text-base text-yellow-800">
                      <AlertTriangle className="w-5 h-5 inline-block align-text-bottom mr-1" aria-hidden="true" />Identificatorul cu numărul <span className="font-bold">{formData.propertyData.nrCadastral}</span> a fost găsit, dar cartea funciară este{' '}
                      <span className="font-bold text-red-600">sistată</span>.
                    </p>
                      {/* Detalii proprietate din ANCPI */}
                      {(formData.propertyData.propertyType || formData.propertyData.address || formData.propertyData.area) && (
                        <div className="text-sm text-gray-600 space-y-0.5">
                          {formData.propertyData.propertyType && (
                            <div className="grid grid-cols-[1.2rem_1fr] gap-x-1 items-baseline">
                              <span><Home className="w-4 h-4 inline-block align-text-bottom" aria-hidden="true" /></span>
                              <span>Tip: <span className="font-medium text-gray-800">{formData.propertyData.propertyType}</span></span>
                            </div>
                          )}
                          {formData.propertyData.address && (
                            <div className="grid grid-cols-[1.2rem_1fr] gap-x-1 items-baseline">
                              <span><MapPin className="w-4 h-4 inline-block align-text-bottom" aria-hidden="true" /></span>
                              <span>Adresă: <span className="font-medium text-gray-800">{formData.propertyData.address}</span></span>
                            </div>
                          )}
                          {formData.propertyData.area && (
                            <div className="grid grid-cols-[1.2rem_1fr] gap-x-1 items-baseline">
                              <span><Ruler className="w-4 h-4 inline-block align-text-bottom" aria-hidden="true" /></span>
                              <span>Suprafață: <span className="font-medium text-gray-800">{formData.propertyData.area} mp</span></span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                      <p className="text-sm text-emerald-800">
                        <CheckCircle2 className="w-4 h-4 inline-block align-text-bottom mr-1" aria-hidden="true" />Vom identifica numărul activ și vom emite extrasul actualizat pentru tine.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-2">
                    <p className="text-base text-emerald-800">
                      <CheckCircle2 className="w-5 h-5 inline-block align-text-bottom mr-1" aria-hidden="true" />Identificatorul cu numărul <span className="font-bold">{formData.propertyData.nrCadastral}</span> a fost găsit!
                    </p>
                    {/* Detalii proprietate din ANCPI */}
                    {(formData.propertyData.propertyType || formData.propertyData.address || formData.propertyData.area) && (
                      <div className="text-sm text-gray-600 space-y-0.5">
                        {formData.propertyData.propertyType && (
                          <div className="grid grid-cols-[1.2rem_1fr] gap-x-1 items-baseline">
                            <span><Home className="w-4 h-4 inline-block align-text-bottom" aria-hidden="true" /></span>
                            <span>Tip: <span className="font-medium text-gray-800">{formData.propertyData.propertyType}</span></span>
                          </div>
                        )}
                        {formData.propertyData.address && (
                          <div className="grid grid-cols-[1.2rem_1fr] gap-x-1 items-baseline">
                            <span><MapPin className="w-4 h-4 inline-block align-text-bottom" aria-hidden="true" /></span>
                            <span>Adresă: <span className="font-medium text-gray-800">{formData.propertyData.address}</span></span>
                          </div>
                        )}
                        {formData.propertyData.area && (
                          <div className="grid grid-cols-[1.2rem_1fr] gap-x-1 items-baseline">
                            <span><Ruler className="w-4 h-4 inline-block align-text-bottom" aria-hidden="true" /></span>
                            <span>Suprafață: <span className="font-medium text-gray-800">{formData.propertyData.area} mp</span></span>
                          </div>
                        )}
                      </div>
                    )}
                    <p className="text-sm text-emerald-800">Continuă pentru a comanda extrasul.</p>
                  </div>
                )}

                <h2 className="text-2xl font-bold text-gray-900">Selectați tipul de extras</h2>

                {/* Carduri compacte pentru selectare tip extras - FĂRĂ PREȚ */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start mt-2">
                  <button
                    onClick={() => {
                      setFormData({ ...formData, certificateType: 'carteFunciara' });
                      setTimeout(() => {
                        if (window.innerWidth < 1024 && continueButtonRef.current) {
                          continueButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }, 200);
                    }}
                    className={`pt-9 pb-4 sm:pb-6 px-3 sm:px-5 border-2 rounded-xl transition-all relative flex flex-col items-center ${
                      formData.certificateType === 'carteFunciara' ? 'border-emerald-600 bg-emerald-50 shadow-md' : 'border-gray-300 hover:border-emerald-300'
                    }`}>
                    <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                      <span className="bg-emerald-600 text-white text-[9px] sm:text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm whitespace-nowrap">
                        <Star className="w-3 h-3 inline-block align-text-bottom mr-0.5 text-yellow-300" fill="currentColor" aria-hidden="true" />Cel mai comandat
                      </span>
                    </div>
                    <FileText className="w-10 h-10 sm:w-12 sm:h-12 mb-2 sm:mb-3 text-emerald-600" />
                    <h3 className="text-2xl font-bold text-center">Carte Funciară</h3>
                    <p className="text-sm text-gray-600 mt-1 sm:mt-2 text-center">Toate informațiile despre imobil</p>
                    <p className="text-xs sm:text-sm text-emerald-700 mt-1 text-center"><CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline-block align-text-bottom mr-1" aria-hidden="true" />Acceptat la notar, bancă, primărie,<br className="sm:hidden" />instanță, ANAF<span className="hidden sm:inline">, furnizori utilități (racordări, branșamente)</span></p>
                    <p className="hidden sm:block text-xs sm:text-sm text-gray-500 mt-1 text-center"><Lock className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline-block align-text-bottom mr-1" aria-hidden="true" />Verificare gratuită inclusă</p>
                  </button>

                  <button
                    onClick={() => {
                      setFormData({ ...formData, certificateType: 'planCadastral' });
                      setTimeout(() => {
                        if (window.innerWidth < 1024 && continueButtonRef.current) {
                          continueButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }, 200);
                    }}
                    className={`py-4 sm:py-6 px-3 sm:px-5 border-2 rounded-xl transition-all flex flex-col items-center ${
                      formData.certificateType === 'planCadastral' ? 'border-emerald-600 bg-emerald-50 shadow-md' : 'border-gray-300 hover:border-emerald-300'
                    }`}>
                    <div className="h-3"></div>
                    <FileText className="w-10 h-10 sm:w-12 sm:h-12 mb-2 sm:mb-3 text-emerald-600" />
                    <h3 className="text-2xl font-bold text-center">Ortofotoplan</h3>
                    <p className="text-sm text-gray-600 mt-1 sm:mt-2 text-center">Hartă cu limite proprietate</p>
                    <p className="text-xs sm:text-sm text-emerald-700 mt-1 text-center"><CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline-block align-text-bottom mr-1" aria-hidden="true" />Acceptat la notar, bancă, primărie,<br className="sm:hidden" />instanță, ANAF<span className="hidden sm:inline">, furnizori utilități (racordări, branșamente)</span></p>
                    <p className="hidden sm:block text-xs sm:text-sm text-gray-500 mt-1 text-center"><Lock className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline-block align-text-bottom mr-1" aria-hidden="true" />Verificare gratuită inclusă</p>
                  </button>
                </div>

                {/* Trust badges */}
                <div className="flex flex-wrap justify-center gap-3 mt-6 text-xs sm:text-sm text-gray-600">
                  <span className="flex items-center gap-1"><Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4" aria-hidden="true" />Livrare în 5 minute</span>
                  <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4" aria-hidden="true" />Document oficial ANCPI</span>
                  <span className="flex items-center gap-1"><Coins className="w-3.5 h-3.5 sm:w-4 sm:h-4" aria-hidden="true" />Preț fix, fără costuri ascunse</span>
                </div>

              </div>
            )}

            {/* Step 1 - Date imobil (formularele) */}
            {step === 1 && (
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Date imobil</h2>

                {/* Form cadastral */}
                {formData.identificationType === 'cadastral' && (
                  <>
                    {/* Input nr. cadastral - PRIMUL (verificarea se face la click Continuare) */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Identificator (Nr. Carte Funciară sau Nr. Cadastral) *
                      </label>
                      <input
                        type="text"
                        value={formData.propertyData.nrCadastral}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData({
                            ...formData,
                            propertyData: { ...formData.propertyData, nrCadastral: value }
                          });
                          setCadastralVerified(null); // Reset verificare când se schimbă numărul
                          setCadastralFormatError(validateCadastralFormat(value)); // Validare live
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && continueButtonRef.current) {
                            e.preventDefault();
                            continueButtonRef.current.click();
                          }
                        }}
                        className={`w-full px-3 py-2 h-10 text-sm border rounded-lg focus:ring-2 focus:ring-teal-500 ${
                          cadastralFormatError ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Ex: 12345-C1-U1, ex: 12345"
                      />
                      {/* Eroare format - validare live */}
                      {cadastralFormatError && (
                        <p className="text-xs text-red-600 mt-1 font-medium">✗ {cadastralFormatError}</p>
                      )}
                    </div>

                    {/* Dropdown județ */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Județ</label>
                      <select
                        value={formData.propertyData.judet}
                        onChange={(e) => {
                          const judet = e.target.value;
                          setFormData({
                            ...formData,
                            propertyData: {
                              ...formData.propertyData,
                              judet: judet,
                              uat: '', // Reset UAT când se schimbă județul
                              uatId: ''
                            }
                          });
                        }}
                        className="w-full px-3 py-2 h-10 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 relative z-20">
                        <option value="">Selectează județul</option>
                        {Object.keys(ANCPI_DATA).map(judet => (
                          <option key={judet} value={judet}>
                            {judet}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Dropdown UAT (se populează după județ) */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Localitate / UAT (Unitate Administrativ Teritorială)</label>
                      <select
                        value={formData.propertyData.uatId}
                        onChange={(e) => {
                          const selectedOption = e.target.options[e.target.selectedIndex];
                          setFormData({
                            ...formData,
                            propertyData: {
                              ...formData.propertyData,
                              uat: selectedOption.text,
                              uatId: selectedOption.value
                            }
                          });
                        }}
                        disabled={!formData.propertyData.judet}
                        className="w-full px-3 py-2 h-10 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100 disabled:cursor-not-allowed relative z-20">
                        <option value="">
                          {formData.propertyData.judet ? 'Selectează UAT-ul' : 'Selectați județul mai întâi...'}
                        </option>
                        {formData.propertyData.judet && ANCPI_DATA[formData.propertyData.judet]?.map(uat => (
                          <option key={uat.id} value={uat.id}>{uat.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Câmp opțional: Necesar la (scop) */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Necesar la (opțional)
                      </label>
                      <input
                        type="text"
                        value={formData.propertyData.necesarLa}
                        onChange={(e) => setFormData({
                          ...formData,
                          propertyData: { ...formData.propertyData, necesarLa: e.target.value }
                        })}
                        placeholder="Ex: informare, bancă, notariat, etc."
                        className="w-full px-3 py-2 h-10 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Pentru ce aveți nevoie de document?</p>
                    </div>
                  </>
                )}

                {/* Form identificare după adresă (NOU) */}
                {formData.identificationType === 'address' && (
                  <>
                    {/* Nume și Prenume Proprietar */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Nume și Prenume Proprietar *
                      </label>
                      <input
                        type="text"
                        value={formData.propertyData.ownerName}
                        onChange={(e) => setFormData({
                          ...formData,
                          propertyData: { ...formData.propertyData, ownerName: e.target.value }
                        })}
                        placeholder="Ex: Popescu Ion"
                        className="w-full px-3 py-2 h-10 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Numele proprietarului așa cum apare în carte</p>
                    </div>

                    {/* Dropdown județ */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Județ *</label>
                      <select
                        value={formData.propertyData.judet}
                        onChange={(e) => {
                          const judet = e.target.value;
                          setFormData({
                            ...formData,
                            propertyData: {
                              ...formData.propertyData,
                              judet: judet,
                              uat: '', // Reset UAT când se schimbă județul
                              uatId: ''
                            }
                          });
                        }}
                        className="w-full px-3 py-2 h-10 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 relative z-20">
                        <option value="">Selectează județul</option>
                        {Object.keys(ANCPI_DATA).map(judet => (
                          <option key={judet} value={judet}>
                            {judet}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Dropdown UAT (se populează după județ) */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Localitate / UAT (Unitate Administrativ Teritorială) *</label>
                      <select
                        value={formData.propertyData.uatId}
                        onChange={(e) => {
                          const selectedOption = e.target.options[e.target.selectedIndex];
                          setFormData({
                            ...formData,
                            propertyData: {
                              ...formData.propertyData,
                              uat: selectedOption.text,
                              uatId: selectedOption.value
                            }
                          });
                        }}
                        disabled={!formData.propertyData.judet}
                        className="w-full px-3 py-2 h-10 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100 disabled:cursor-not-allowed relative z-20">
                        <option value="">
                          {formData.propertyData.judet ? 'Selectează UAT-ul' : 'Selectați județul mai întâi...'}
                        </option>
                        {formData.propertyData.judet && ANCPI_DATA[formData.propertyData.judet]?.map(uat => (
                          <option key={uat.id} value={uat.id}>{uat.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Adresa completă (textarea) */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Adresa Completă a Imobilului *
                      </label>
                      <textarea
                        value={formData.propertyData.fullAddress}
                        onChange={(e) => setFormData({
                          ...formData,
                          propertyData: { ...formData.propertyData, fullAddress: e.target.value }
                        })}
                        placeholder="Ex: Str. Mihai Eminescu, nr. 10, bl. A1, sc. B, et. 3, ap. 15"
                        rows={2}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 resize-none"
                      />
                      <p className="text-xs text-gray-500 mt-1">Adresa completă cu stradă, număr, bloc, scară, etaj, apartament</p>
                    </div>

                    {/* Câmp opțional: Necesar la (scop) */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Necesar la (opțional)
                      </label>
                      <input
                        type="text"
                        value={formData.propertyData.necesarLa}
                        onChange={(e) => setFormData({
                          ...formData,
                          propertyData: { ...formData.propertyData, necesarLa: e.target.value }
                        })}
                        placeholder="Ex: informare, bancă, notariat, etc."
                        className="w-full px-3 py-2 h-10 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Pentru ce aveți nevoie de document?</p>
                    </div>

                    {/* Alert pentru identificare după adresă */}
                    <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
                      <p className="text-sm text-yellow-800">
                        <strong><AlertTriangle className="w-4 h-4 inline-block align-text-bottom mr-1" aria-hidden="true" />Atenție:</strong> Serviciul de identificare după adresă este disponibil DOAR pentru imobile intabulate.
                        Costul este de <strong className="text-emerald-700">200 RON</strong> și include identificarea numărului cadastral și emiterea extrasului.
                        Procesarea durează până la 3 zile lucrătoare, dar de obicei livrăm în primele ore după finalizarea plății.
                      </p>
                    </div>
                  </>
                )}

              </div>
            )}

            {/* Step 3 - Facturare și Contact */}
            {step === 3 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Unde trimitem factura și extrasul?</h2>
                {/* Tabs pentru selectare tip facturare */}
                <div className="flex border-b border-gray-200 mb-6">
                  <button
                    onClick={() => {
                      const savedPfData = localStorage.getItem('cf_extras_pf_data');
                      const pfData = savedPfData ? JSON.parse(savedPfData) : {};
                      const street = pfData.street || '';
                      const streetNumber = pfData.streetNumber || '';
                      const fullAddress = street && streetNumber ? `Str. ${street}, Nr. ${streetNumber}` : '';
                      setFormData({
                        ...formData,
                        userType: 'individual',
                        billingType: 'individual',
                        billingData: {
                          type: 'individual',
                          name: pfData.name || '',
                          county: pfData.county || '',
                          city: pfData.city || '',
                          street: pfData.street || '',
                          streetNumber: pfData.streetNumber || '',
                          address: fullAddress
                        },
                        contactData: {
                          ...formData.contactData,
                          email: pfData.email || '',
                          phone: pfData.phone || ''
                        }
                      });
                      setTimeout(() => triggerFlash('billing-name-input'), 100);
                    }}
                    className={`flex-1 py-3 text-center font-semibold transition-all ${
                      formData.billingType === 'individual'
                        ? 'text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}>
                    <User className="w-4 h-4 inline-block align-text-bottom mr-1" aria-hidden="true" />Persoană fizică
                  </button>
                  <button
                    onClick={() => {
                      // Șterge datele firmei și localStorage - să verifice din nou
                      localStorage.removeItem('cf_extras_pj_data');
                      setFormData({
                        ...formData,
                        userType: 'company',
                        billingType: 'otherCompany',
                        billingData: {
                          cui: '',
                          name: '',
                          registrationNumber: '',
                          address: ''
                        },
                        contactData: {
                          ...formData.contactData,
                          fullName: formData.contactData.fullName || '',
                          email: formData.contactData.email || '',
                          phone: formData.contactData.phone || ''
                        }
                      });
                      setTimeout(() => triggerFlash('billing-cui-input'), 100);
                    }}
                    className={`flex-1 py-3 text-center font-semibold transition-all ${
                      formData.billingType === 'otherCompany'
                        ? 'text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}>
                    <Building2 className="w-4 h-4 inline-block align-text-bottom mr-1" aria-hidden="true" />Firmă
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Formular PJ */}
                  {formData.billingType === 'otherCompany' && (
                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">CUI Firmă pentru facturare</label>
                        <div className="flex flex-col sm:flex-row gap-3">
                          <input
                            id="billing-cui-input"
                            type="text"
                            value={formData.billingData.cui || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              billingData: { ...formData.billingData, cui: e.target.value }
                            })}
                            onFocus={() => {
                              // Șterge localStorage și resetează datele firmei când utilizatorul vrea alt CUI
                              localStorage.removeItem('cf_extras_pj_data');
                              setFormData({
                                ...formData,
                                billingData: { cui: '' } // Reset complet
                              });
                            }}
                            onKeyPress={(e) => e.key === 'Enter' && handleVerifyCUI(formData.billingData.cui, true)}
                            className={`flex-1 px-3 py-2 h-10 text-sm border rounded-lg focus:ring-2 focus:ring-teal-500 transition-all ${
                              flashElement === 'billing-cui-input' ? 'animate-gentle-flash' : 'border-gray-300'
                            }`}
                            placeholder="RO87654321"
                          />
                          <button
                            onClick={() => handleVerifyCUI(formData.billingData.cui, true)}
                            disabled={isVerifyingCUI || !formData.billingData.cui}
                            className="px-6 sm:px-8 py-3 min-h-[48px] bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 font-semibold flex items-center justify-center gap-2 text-sm sm:text-base whitespace-nowrap">
                            {isVerifyingCUI ? (
                              <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Se verifică...
                              </>
                            ) : (
                              'Caută firmă'
                            )}
                          </button>
                        </div>
                      </div>

                      {formData.billingData.name && (
                        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                          <h4 className="font-semibold text-green-900 mb-2 flex items-center">
                            <CheckCircle className="w-5 h-5 mr-2" />
                            Date firmă pentru facturare:
                          </h4>
                          <div className="space-y-1 text-sm">
                            <p><strong>Nume:</strong> {formData.billingData.name}</p>
                            <p><strong>CUI:</strong> {formData.billingData.cui}</p>
                            <p><strong>Nr. Reg. Com.:</strong> {formData.billingData.registrationNumber}</p>
                            <p><strong>Adresă:</strong> {formData.billingData.address}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Formular PF */}
                  {formData.billingType === 'individual' && (
                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Nume și Prenume</label>
                        <input
                          id="billing-name-input"
                          type="text"
                          value={formData.billingData.name || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            billingData: { ...formData.billingData, type: 'individual', name: e.target.value }
                          })}
                          className={`w-full px-3 py-2 h-10 text-sm border rounded-lg focus:ring-2 focus:ring-teal-500 transition-all ${
                            flashElement === 'billing-name-input' ? 'animate-gentle-flash' : 'border-gray-300'
                          }`}
                          placeholder="Ion Popescu"
                        />
                        <p className="text-xs text-gray-500 mt-1">Minim 3 caractere</p>
                      </div>

                      {/* Grid Județ + Localitate (2 coloane pe desktop, 1 pe mobil) */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Dropdown Județ */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Județ</label>
                          <select
                            id="billing-county-select"
                            value={formData.billingData.county || ''}
                            onChange={(e) => {
                              const county = e.target.value;
                              setFormData({
                                ...formData,
                                billingData: {
                                  ...formData.billingData,
                                  county: county,
                                  city: '' // Reset city când se schimbă județul
                                }
                              });
                            }}
                            className={`w-full px-3 py-2 h-10 text-sm border rounded-lg focus:ring-2 focus:ring-teal-500 transition-all ${
                              flashElement === 'billing-county-select' ? 'animate-gentle-flash' : 'border-gray-300'
                            }`}>
                            <option value="">Selectează județul</option>
                            {Object.keys(ANCPI_DATA).map(judet => (
                              <option key={judet} value={judet}>
                                {judet}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Dropdown Localitate (UAT) */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Localitate</label>
                          <select
                            id="billing-city-select"
                            value={formData.billingData.city || ''}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                billingData: { ...formData.billingData, city: e.target.value }
                              });
                            }}
                            disabled={!formData.billingData.county}
                            className={`w-full px-3 py-2 h-10 text-sm border rounded-lg focus:ring-2 focus:ring-teal-500 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed ${
                              flashElement === 'billing-city-select' ? 'animate-gentle-flash' : 'border-gray-300'
                            }`}>
                            <option value="">
                              {formData.billingData.county ? 'Selectează localitatea' : 'Selectați județul mai întâi...'}
                            </option>
                            {formData.billingData.county && ANCPI_DATA[formData.billingData.county]?.map(uat => {
                              // Pentru București, salvăm doar "Sectorul X", nu "Bucuresti Sectorul X"
                              const displayName = uat.name;
                              const saveValue = formData.billingData.county === 'BUCUREȘTI'
                                ? uat.name.replace('Bucuresti ', '')
                                : uat.name;

                              return (
                                <option key={uat.id} value={saveValue}>
                                  {displayName}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      </div>

                      {/* Grid Strada + Număr (70% | 30%) */}
                      <div className="grid grid-cols-1 md:grid-cols-[70%_30%] gap-4">
                        {/* Input Strada */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Strada</label>
                          <input
                            id="billing-street-input"
                            type="text"
                            value={formData.billingData.street || ''}
                            onChange={(e) => {
                              const street = e.target.value;
                              const number = formData.billingData.streetNumber || '';
                              const fullAddress = street && number ? `Str. ${street}, Nr. ${number}` : '';

                              setFormData({
                                ...formData,
                                billingData: {
                                  ...formData.billingData,
                                  street: street,
                                  address: fullAddress
                                }
                              });
                            }}
                            className={`w-full px-3 py-2 h-10 text-sm border rounded-lg focus:ring-2 focus:ring-teal-500 transition-all ${
                              flashElement === 'billing-street-input' ? 'animate-gentle-flash' : 'border-gray-300'
                            }`}
                            placeholder="Mihai Viteazu"
                          />
                        </div>

                        {/* Input Număr */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Număr</label>
                          <input
                            id="billing-number-input"
                            type="text"
                            value={formData.billingData.streetNumber || ''}
                            onChange={(e) => {
                              const number = e.target.value;
                              const street = formData.billingData.street || '';
                              const fullAddress = street && number ? `Str. ${street}, Nr. ${number}` : '';

                              setFormData({
                                ...formData,
                                billingData: {
                                  ...formData.billingData,
                                  streetNumber: number,
                                  address: fullAddress
                                }
                              });
                            }}
                            className={`w-full px-3 py-2 h-10 text-sm border rounded-lg focus:ring-2 focus:ring-teal-500 transition-all ${
                              flashElement === 'billing-number-input' ? 'animate-gentle-flash' : 'border-gray-300'
                            }`}
                            placeholder="10"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Date Contact - afișate DOAR după ce selectează PJ/PF */}
                {formData.billingType && (
                  <div className="mt-6">

                  {/* Afișează "Nume și Prenume" DOAR pentru PJ (nu pentru PF - deja l-au introdus la facturare) */}
                  {formData.billingType !== 'individual' && (
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Nume și Prenume *</label>
                      <input
                        id="contact-name-input"
                        type="text"
                        value={formData.contactData.fullName}
                        onChange={(e) => {
                          const newName = e.target.value;
                          setFormData({
                            ...formData,
                            contactData: { ...formData.contactData, fullName: newName }
                          });
                          validateNameLive(newName);
                        }}
                        className={`w-full px-3 py-2 h-10 text-sm border rounded-lg transition-all ${
                          flashElement === 'contact-name-input' ? 'animate-gentle-flash' :
                          formData.contactData.fullName && nameError ? 'border-red-500 bg-red-50 focus:ring-red-500' :
                          formData.contactData.fullName && !nameError && formData.contactData.fullName.trim().length >= 3 ? 'border-green-500 bg-green-50 focus:ring-green-500' :
                          'border-gray-300 focus:ring-teal-500'
                        }`}
                        placeholder="Ion Popescu"
                      />
                      {formData.contactData.fullName && nameError ? (
                        <p className="text-xs text-red-600 mt-1 font-medium"><AlertTriangle className="w-3.5 h-3.5 inline-block align-text-bottom mr-1" aria-hidden="true" />{nameError}</p>
                      ) : formData.contactData.fullName && !nameError && formData.contactData.fullName.trim().split(/\s+/).filter(w => w.length > 0).length >= 2 ? (
                        <p className="text-xs text-green-600 mt-1 font-medium">✓ Nume valid</p>
                      ) : (
                        <p className="text-xs text-gray-500 mt-1">Introduceți Nume și Prenume (fiecare cuvânt minim 3 caractere)</p>
                      )}
                    </div>
                  )}

                  {/* Grid Email | Telefon */}
                  <div className="grid grid-cols-1 md:grid-cols-[60%_40%] gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                      <input
                        id="contact-email-input"
                        type="email"
                        value={formData.contactData.email}
                        onChange={(e) => {
                          const newEmail = e.target.value;
                          setFormData({
                            ...formData,
                            contactData: { ...formData.contactData, email: newEmail }
                          });
                          validateEmailLive(newEmail);
                        }}
                        className={`w-full px-3 py-2 h-10 text-sm border rounded-lg transition-all ${
                          flashElement === 'contact-email-input' ? 'animate-gentle-flash' :
                          formData.contactData.email && emailError ? 'border-red-500 bg-red-50 focus:ring-red-500' :
                          formData.contactData.email && !emailError ? 'border-green-500 bg-green-50 focus:ring-green-500' :
                          'border-gray-300 focus:ring-teal-500'
                        }`}
                        placeholder="ion.popescu@email.ro"
                      />
                      {formData.contactData.email && emailError ? (
                        <p className="text-xs text-red-600 mt-1 font-medium"><AlertTriangle className="w-3.5 h-3.5 inline-block align-text-bottom mr-1" aria-hidden="true" />{emailError}</p>
                      ) : formData.contactData.email && !emailError ? (
                        <p className="text-xs text-green-600 mt-1 font-medium">✓ Email valid</p>
                      ) : null}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Telefon (opțional)</label>
                      <input
                        type="tel"
                        value={formData.contactData.phone}
                        onChange={(e) => {
                          const newPhone = e.target.value;
                          setFormData({
                            ...formData,
                            contactData: { ...formData.contactData, phone: newPhone }
                          });
                          validatePhoneLive(newPhone);
                        }}
                        className={`w-full px-3 py-2 h-10 text-sm border rounded-lg transition-all ${
                          formData.contactData.phone && phoneError ? 'border-red-500 bg-red-50 focus:ring-red-500' :
                          formData.contactData.phone && !phoneError ? 'border-green-500 bg-green-50 focus:ring-green-500' :
                          'border-gray-300 focus:ring-teal-500'
                        }`}
                        placeholder="0721234567"
                      />
                      {formData.contactData.phone && phoneError ? (
                        <p className="text-xs text-red-600 mt-1 font-medium"><AlertTriangle className="w-3.5 h-3.5 inline-block align-text-bottom mr-1" aria-hidden="true" />{phoneError}</p>
                      ) : formData.contactData.phone && !phoneError ? (
                        <p className="text-xs text-green-600 mt-1 font-medium">✓ Valid</p>
                      ) : null}
                    </div>
                  </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 4 - Confirmare comandă */}
            {step === 4 && (
              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Confirmare comandă</h2>
                
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200">
                  {appliedDiscount && isSimpleOrder ? (
                    <div className="text-center mb-2">
                      <p className="text-sm text-gray-600 mb-1">Total comandă:</p>
                      <span className="block text-lg font-semibold text-gray-400 line-through">
                        {formatPrice(formData.certificateType === 'carteFunciara' ? (prices.carteFunciara || prices.basic) : (prices.planCadastral || prices.withHistory))} RON
                      </span>
                      <span className="block text-3xl font-bold text-blue-900">{formatPrice(appliedDiscount.newPrice)} RON</span>
                      <p className="text-sm text-emerald-700 font-medium mt-1">
                        Cod {appliedDiscount.code} aplicat: −{formatPrice(appliedDiscount.discountAmount)} RON
                      </p>
                    </div>
                  ) : (
                    <h3 className="text-3xl font-bold text-center text-blue-900 mb-2">
                      Total comandă: <br className="sm:hidden" />{formatPrice(
                        formData.identificationType === 'address'
                          ? (prices.identificareAdresa || 200)  // Identificare după adresă
                          : cadastralStatus === 'Sistata' && wantsUpdatedNumber
                            ? (prices.upgradeSistata || 200)  // Upgrade Sistată
                            : (formData.certificateType === 'carteFunciara' ? (prices.carteFunciara || prices.basic) : (prices.planCadastral || prices.withHistory))
                      )} RON
                    </h3>
                  )}
                  <p className="text-center text-sm text-gray-600">- taxa ANCPI inclusă -</p>
                </div>

                {/* Cod de reducere (DOAR comenzi CF/PC simple) */}
                {isSimpleOrder && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ai un cod de reducere?</label>
                    {appliedDiscount ? (
                      <div className="flex items-center justify-between gap-3 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
                        <span className="text-sm text-emerald-800 font-medium">
                          ✓ Cod <span className="font-mono font-bold">{appliedDiscount.code}</span> aplicat
                        </span>
                        <button type="button" onClick={handleRemoveDiscount}
                          className="text-sm text-gray-500 hover:text-red-600 font-medium flex-shrink-0">
                          Elimină
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <input
                            type="text"
                            value={discountCode}
                            maxLength={20}
                            onChange={(e) => { setDiscountCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')); setDiscountError(''); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApplyDiscount(); } }}
                            placeholder="Introdu codul"
                            className="flex-1 px-4 py-3 min-h-[48px] border-2 border-gray-300 rounded-lg text-sm uppercase focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          />
                          <button
                            type="button"
                            onClick={handleApplyDiscount}
                            disabled={discountLoading || !discountCode.trim()}
                            className="w-full sm:w-auto px-6 py-3 min-h-[48px] bg-emerald-600 text-white rounded-lg font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50">
                            {discountLoading ? 'Se verifică...' : 'Aplică'}
                          </button>
                        </div>
                        {discountError && <p className="text-sm text-red-600 mt-2">{discountError}</p>}
                      </>
                    )}
                  </div>
                )}

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="font-semibold mb-4">Detalii comandă:</h4>

                  <div className="space-y-2 text-sm">
                    <div className="flex flex-col py-2 border-b">
                      <span className="text-gray-600">Tip utilizator:</span>
                      <span className="font-medium">
                        {formData.userType === 'company' ? 'Persoană Juridică' : 'Persoană Fizică'}
                      </span>
                    </div>

                    {formData.companyData && (
                      <div className="flex flex-col py-2 border-b">
                        <span className="text-gray-600">Firmă:</span>
                        <span className="font-medium">{formData.companyData.name}</span>
                      </div>
                    )}

                    {/* Afișează "Tip extras" DOAR când NU e identificare după adresă ȘI NU e sistată cu upgrade */}
                    {formData.identificationType !== 'address' && !(cadastralStatus === 'Sistata' && wantsUpdatedNumber) && (
                      <div className="flex flex-col py-2 border-b">
                        <span className="text-gray-600">Tip extras:</span>
                        <span className="font-medium">
                          {formData.certificateType === 'carteFunciara' ? 'Extras Carte Funciară' : 'Extras Plan Cadastral'}
                        </span>
                      </div>
                    )}

                    {/* Imobilul comandat - clientul vede exact ce plătește (important și la revenirea din email) */}
                    {(formData.propertyData?.judet || formData.propertyData?.uat) && (
                      <div className="flex flex-col py-2 border-b">
                        <span className="text-gray-600">Imobil:</span>
                        <span className="font-medium">
                          {[formData.propertyData.judet, formData.propertyData.uat].filter(Boolean).join(', ')}
                        </span>
                      </div>
                    )}
                    {formData.identificationType !== 'address' && formData.propertyData?.nrCadastral && (
                      <div className="flex flex-col py-2 border-b">
                        <span className="text-gray-600">Număr cadastral / CF:</span>
                        <span className="font-medium break-all">{formData.propertyData.nrCadastral}</span>
                      </div>
                    )}

                    {/* Afișează Identificare după adresă (dacă aplicabil) */}
                    {formData.identificationType === 'address' && (
                      <div className="flex flex-col py-2 border-b bg-blue-50">
                        <span className="text-gray-600">Serviciu:</span>
                        <span className="font-medium text-emerald-700">Identificare imobil după adresă</span>
                      </div>
                    )}

                    {/* Afișează Upgrade Sistată (dacă aplicabil) */}
                    {cadastralStatus === 'Sistata' && wantsUpdatedNumber && (
                      <div className="flex flex-col py-2 border-b bg-yellow-50">
                        <span className="text-gray-600">Serviciu:</span>
                        <span className="font-medium text-emerald-700">Extras actualizat (carte sistată)</span>
                      </div>
                    )}

                    {formData.billingType === 'individual' && formData.billingData?.name && (
                      <div className="flex flex-col py-2 border-b">
                        <span className="text-gray-600">Facturare pe:</span>
                        <span className="font-medium">{formData.billingData.name}</span>
                      </div>
                    )}

                    <div className="flex flex-col py-2 border-b">
                      <span className="text-gray-600">Email:</span>
                      <span className="font-medium break-all">{formData.contactData.email}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800 text-center">
                    ✓ Livrare în câteva minute<br/>
                    Solicitările se procesează automat, imediat după confirmarea plății.
                  </p>
                </div>

                {/* FIX #27 - Checkbox-uri Termeni și Marketing */}
                <div className="space-y-4">
                  {/* Checkbox 1 - Termeni și Condiții (OBLIGATORIU) */}
                  <div
                    id="terms-checkbox"
                    className={`flex items-center justify-center gap-3 p-4 rounded-lg border transition-all ${
                      flashElement === 'terms-checkbox' ? 'animate-gentle-flash bg-gray-50 border-gray-300' : 'bg-gray-50 border-gray-300'
                    }`}>
                    <input
                      type="checkbox"
                      id="acceptTerms"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-2 focus:ring-teal-500 cursor-pointer flex-shrink-0"
                    />
                    <label htmlFor="acceptTerms" className="text-sm text-gray-700 cursor-pointer">
                      Am citit și sunt de acord cu <a href="/termeni-conditii" target="_blank" className="text-emerald-600 hover:underline font-semibold">Termenii și Condițiile</a> *
                    </label>
                  </div>

                  {/* Checkbox 2 - Comunicări comerciale (OPȚIONAL) */}
                  <div className="flex items-center justify-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <input
                      type="checkbox"
                      id="acceptMarketing"
                      checked={acceptedMarketing}
                      onChange={(e) => setAcceptedMarketing(e.target.checked)}
                      className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-2 focus:ring-teal-500 cursor-pointer flex-shrink-0"
                    />
                    <label htmlFor="acceptMarketing" className="text-sm text-gray-700 cursor-pointer">
                      Sunt de acord să primesc comunicări comerciale (oferte, noutăți, promoții)
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between sm:items-center mt-8 pt-6 border-t border-gray-200">
              {/* Buton Înapoi - pe mobile apare jos, pe desktop stânga */}
              <div className="w-full sm:w-auto">
                {step > 1 && (
                  <button
                    onClick={() => {
                      // Log analytics în backend
                      const identifier = formData.cui || formData.cnp || 'UNKNOWN';
                      fetch(`${API_URL}/api/orders/analytics/log`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          event: `🔙 [WIZARD] ${identifier} - Înapoi: Step ${step} → ${step - 1}`,
                          data: { identifier, from: step, to: step - 1 }
                        })
                      }).catch(() => {});

                      setStep(step - 1);
                      setToast(null);
                      setAppliedDiscount(null);
                      setDiscountCode('');
                      setDiscountError('');
                    }}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 sm:px-8 py-3 min-h-[48px] border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-semibold text-sm sm:text-base">
                    <ChevronLeft className="w-5 h-5" />
                    Înapoi
                  </button>
                )}
              </div>

              {/* Buton Mai departe / Plătește - pe mobile apare sus, pe desktop dreapta */}
              <div className="w-full sm:w-auto">
                {step < 4 ? (
                  <div className="flex flex-col-reverse sm:flex-row items-center gap-3">
                  {isVerifyingCadastral && step === 1 && (
                    <span className="text-emerald-600 font-medium flex items-center gap-2 text-sm">
                      <Search className="w-4 h-4" aria-hidden="true" />Verificare în curs... {verificationSeconds}s
                    </span>
                  )}
                  <button
                    ref={continueButtonRef}
                    onClick={async () => {
                      // Step 1: Date imobil - verificare cadastral la click Continuare
                      if (step === 1 && formData.identificationType === 'cadastral') {
                        // Verificare câmpuri completate
                        if (!formData.propertyData.judet || !formData.propertyData.uat || !formData.propertyData.uatId) {
                          setToast({ message: 'Completați județul și UAT-ul', type: 'error' });
                          return;
                        }
                        if (!formData.propertyData.nrCadastral.trim()) {
                          setToast({ message: 'Introduceți numărul cadastral', type: 'error' });
                          return;
                        }

                        // Start verificare
                        setIsVerifyingCadastral(true);
                        setToast(null);

                        try {
                          const response = await fetch(`${API_URL}/api/orders/verify-cadastral`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              judet: formData.propertyData.judet,
                              uat: formData.propertyData.uat,
                              uatId: formData.propertyData.uatId,
                              nrCadastral: formData.propertyData.nrCadastral
                            })
                          });

                          const data = await response.json();

                          if (data.success && data.exists) {
                            setCadastralVerified(true);
                            setCadastralStatus(data.data?.status);

                            // Salvează detalii proprietate din ANCPI (dacă există)
                            if (data.data) {
                              setFormData(prev => ({
                                ...prev,
                                propertyData: {
                                  ...prev.propertyData,
                                  propertyType: data.data.propertyType || null,
                                  address: data.data.address || null,
                                  area: data.data.area || null
                                }
                              }));
                            }

                            // Dacă e sistată - afișăm modal
                            if (data.data?.status === 'Sistata') {
                              setNewCadastralNumber(data.data?.newCadastralNumber || null);
                              setShowSistataModal(true);
                              setIsVerifyingCadastral(false);
                              return; // Nu continuăm - așteptăm decizia din modal
                            }

                            // Dacă e Activă - continuăm la step 2
                            setToast({ message: 'Număr cadastral valid', type: 'success' });

                            // Log analytics
                            fetch(`${API_URL}/api/orders/analytics/log`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                event: `[WIZARD STEP 1→2] Imobil: ${formData.propertyData.judet}, ${formData.propertyData.uat}, Nr. ${formData.propertyData.nrCadastral} (Activ)`,
                                data: {
                                  judet: formData.propertyData.judet,
                                  uat: formData.propertyData.uat,
                                  nrCadastral: formData.propertyData.nrCadastral,
                                  status: 'Activa'
                                }
                              })
                            }).catch(() => {});

                            setStep(2);
                            setIsVerifyingCadastral(false);

                            // Mobile scroll
                            setTimeout(() => {
                              if (window.innerWidth < 1024 && progressBarRef.current) {
                                progressBarRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              }
                            }, 100);
                            return;
                          } else {
                            setCadastralVerified(false);
                            setToast({ message: data.message || 'Numărul cadastral nu a fost găsit', type: 'error' });
                            setIsVerifyingCadastral(false);
                            return;
                          }
                        } catch (err) {
                          console.error('Error verifying cadastral:', err);
                          setCadastralVerified(false);
                          setToast({ message: 'Eroare la verificarea numărului cadastral', type: 'error' });
                          setIsVerifyingCadastral(false);
                          return;
                        }
                      }

                      // FIX #27 - Validare explicită cu mesaj de eroare pentru PF (Pas 3 - Date factura + Contact)
                      if (step === 3) {
                        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                        const phoneRegex = /^(07|02)\d{8}$/;
                        const email = formData.contactData.email || '';
                        const phone = (formData.contactData.phone || '').trim();

                        // Validare date facturare PF
                        if (formData.billingType === 'individual') {
                          const name = (formData.billingData.name || '').trim();
                          const address = (formData.billingData.address || '').trim();
                          if (name.length < 3) {
                            setToast({ message: 'Nume și prenume trebuie să conțină minim 3 caractere', type: 'error' });
                            return;
                          }
                          if (address.length < 5) {
                            setToast({ message: 'Adresa trebuie să conțină minim 5 caractere (ex: Str. Mihai Viteazu, Nr. 10, București)', type: 'error' });
                            return;
                          }
                        }

                        // Validare date contact (DOAR pentru PJ - PF nu are fullName separat)
                        if (formData.billingType !== 'individual') {
                          const fullName = (formData.contactData.fullName || '').trim();
                          if (fullName.length < 3) {
                            setToast({ message: 'Nume și prenume trebuie să conțină minim 3 caractere', type: 'error' });
                            return;
                          }
                        }

                        // Validare email (obligatoriu pentru toți)
                        if (!email || !emailRegex.test(email)) {
                          setToast({ message: 'Adresă email invalidă. Verificați dacă nu există puncte duble sau caractere greșite (ex: nume@yahoo.com)', type: 'error' });
                          return;
                        }

                        // Validare telefon (opțional, dar dacă e completat trebuie valid)
                        if (phone && !phoneRegex.test(phone)) {
                          setToast({ message: 'Format telefon invalid. Folosiți format românesc: 07... sau 02... (fără +40)', type: 'error' });
                          return;
                        }
                      }

                      // Log analytics detaliat în backend (per step)
                      let logMessage = '';
                      let logData = {};

                      if (step === 2) {
                        // Step 2: Tip extras
                        const certType = formData.certificateType === 'carteFunciara' ? 'Carte Funciară' :
                                        formData.certificateType === 'planCadastral' ? 'Plan Cadastral' : '?';
                        logMessage = `[WIZARD STEP 2→3] Alegere: ${certType}`;
                        logData = { certificateType: certType };
                        trackEvent('select_item', {
                          item_list_name: 'Tip extras',
                          items: [buildItem(formData.certificateType, getCurrentPrice(), getVariant())]
                        });
                      } else if (step === 3) {
                        // Step 3: Facturare + Contact
                        const billingType = formData.billingType === 'otherCompany' ? 'PJ (altă firmă)' :
                                           formData.billingType === 'individual' ? 'PF' :
                                           formData.billingType === 'sameCompany' ? 'PJ (aceeași firmă)' : '?';
                        // FĂRĂ email în log (date personale)
                        logMessage = `[WIZARD STEP 3→4] Facturare: ${billingType}`;
                        logData = {
                          billingType: billingType,
                          hasPhone: !!formData.contactData.phone
                        };
                        // Salvează comanda nefinalizată (fire-and-forget; eșecul nu afectează wizard-ul)
                        fetch(`${API_URL}/api/orders/draft`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            token: draftTokenRef.current || undefined,
                            certificateType: formData.certificateType,
                            certificatePurpose: formData.certificatePurpose,
                            userType: formData.userType,
                            billingType: formData.billingType,
                            cui: formData.cui,
                            propertyData: formData.propertyData,
                            companyData: formData.companyData,
                            billingData: formData.billingData,
                            contactData: formData.contactData
                          })
                        })
                          .then((r) => r.json())
                          .then((d) => { if (d && d.success && d.token) draftTokenRef.current = d.token; })
                          .catch(() => {});
                      }

                      if (logMessage) {
                        fetch(`${API_URL}/api/orders/analytics/log`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            event: logMessage,
                            data: logData
                          })
                        }).catch(() => {});
                      }

                      // Când intră în step 3 (Facturare), pre-selectează PF mereu
                      if (step + 1 === 3) {
                        const savedPfData = localStorage.getItem('cf_extras_pf_data');
                        const pfData = savedPfData ? JSON.parse(savedPfData) : {};
                        const street = pfData.street || '';
                        const streetNumber = pfData.streetNumber || '';
                        const fullAddress = street && streetNumber ? `Str. ${street}, Nr. ${streetNumber}` : '';
                        setFormData(prev => ({
                          ...prev,
                          userType: 'individual',
                          billingType: 'individual',
                          billingData: {
                            type: 'individual',
                            name: pfData.name || '',
                            county: pfData.county || '',
                            city: pfData.city || '',
                            street: pfData.street || '',
                            streetNumber: pfData.streetNumber || '',
                            address: fullAddress
                          },
                          contactData: {
                            ...prev.contactData,
                            email: pfData.email || '',
                            phone: pfData.phone || ''
                          }
                        }));
                      }

                      setStep(step + 1);
                      setToast(null);

                      // Mobile: Scroll smooth după schimbare step - mereu în topul formularului
                      setTimeout(() => {
                        if (window.innerWidth < 1024 && progressBarRef.current) {
                          progressBarRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }, 100);
                    }}
                    disabled={!canProceed() || maintenanceMode || isVerifyingCadastral}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 sm:px-8 py-3 min-h-[48px] bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold text-sm sm:text-base">
                    {isVerifyingCadastral ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Verificare...
                      </>
                    ) : maintenanceMode ? 'Mentenanță...' : 'Continuare'}
                    {!isVerifyingCadastral && <ChevronRight className="w-5 h-5" />}
                  </button>
                </div>
                ) : (
                  <button
                    ref={paymentButtonRef}
                    onClick={() => {
                      // FIX #27 - Verificare Termeni și Condiții înainte de plată
                      if (!acceptedTerms) {
                        setToast({ message: 'Trebuie să acceptați Termenii și Condițiile pentru a continua', type: 'error' });
                        return;
                      }
                      handleSubmitOrder();
                    }}
                    disabled={isVerifyingCUI || maintenanceMode}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 sm:px-8 py-3 min-h-[48px] bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold text-sm sm:text-base">
                    <CreditCard className="w-5 h-5" />
                    {isVerifyingCUI ? 'Se procesează...' : 'Plătește cu card'}
                  </button>
                )}
              </div>
            </div>
          </div>
          {/* Închide Form Content */}
          {/* Închide layout intern wizard (progress + form) */}
          </div>
        </div>

          {/* Coloana DREAPTA - Specimen Image (35%) - Vizibil doar la step 1 pe desktop */}
          {/* Animație: fade-out + slide dreapta când trece la step 2+ */}
          <div className={`order-1 lg:order-3 relative z-0 rounded-2xl overflow-hidden shadow-lg bg-white
            transition-all duration-500 ease-in-out
            ${step === 1
              ? 'opacity-100 translate-x-0 lg:block'
              : 'lg:opacity-0 lg:translate-x-10 lg:pointer-events-none lg:invisible'
            }`}>
            <img
              src="/carte-funciara-2-specimen.webp"
              alt="Specimen Extras Carte Funciară"
              className="w-full h-auto object-contain"
              width="1653"
              height="1936"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-red-600 font-bold text-2xl sm:text-3xl tracking-widest transform -rotate-45 drop-shadow-lg opacity-90">
                SPECIMEN
              </span>
            </div>
          </div>

        {/* Închide grid layout 25/55/20 */}
        </div>
      </div>

      <Pricing prices={prices} onOrderClick={scrollToWizard} />
      <TrustSection />
      <About />
      <HomeGuide onOrderClick={scrollToWizard} />
      <FAQ />
      <Footer />

      {/* Modal Sistată */}
      <SistataModal
        isOpen={showSistataModal}
        onContinue={() => {
          // User acceptă upgrade - continuăm la step 2
          setShowSistataModal(false);
          setWantsUpdatedNumber(true);
          setToast({ message: '✓ Extras actualizat selectat', type: 'success' });

          // Log analytics
          fetch(`${API_URL}/api/orders/analytics/log`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              event: `[WIZARD STEP 1→2] Imobil: ${formData.propertyData.judet}, ${formData.propertyData.uat}, Nr. ${formData.propertyData.nrCadastral} (Sistată + Upgrade)`,
              data: {
                judet: formData.propertyData.judet,
                uat: formData.propertyData.uat,
                nrCadastral: formData.propertyData.nrCadastral,
                status: 'Sistata',
                upgrade: true
              }
            })
          }).catch(() => {});

          setStep(2);

          // Mobile scroll
          setTimeout(() => {
            if (window.innerWidth < 1024 && progressBarRef.current) {
              progressBarRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        }}
        onCancel={() => {
          setShowSistataModal(false);
          setWantsUpdatedNumber(false);
        }}
        upgradePrice={prices.upgradeSistata || 200}
        wantsUpgrade={wantsUpdatedNumber}
        setWantsUpgrade={setWantsUpdatedNumber}
        nrCadastral={formData.propertyData.nrCadastral}
        judet={formData.propertyData.judet}
        uat={formData.propertyData.uat}
        propertyType={formData.propertyData.propertyType}
        address={formData.propertyData.address}
        area={formData.propertyData.area}
      />

      {/* Toast Notification (floating bottom) */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default CertificateWizard;
